import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { Router } from '@angular/router';
import { isEmptyObject, parseEmptyArray } from '@common/helpers';
import { PREVIOUS_PAGE_IDENTIFIER, ServicingCommonModel } from '@common/interfaces';
import {
  MealCatalogItem,
  PassengerApplicationStatusMap,
  SegmentMap,
  UpdateFlightMealInformation,
} from '@common/interfaces/mybooking/flight-meal.interface';
import { MealInformation } from '@common/interfaces/mybooking/mybooking.interface';
import { AppliedMealList, Meal, TravelerMealInfo } from '@common/interfaces/mybooking/order-services.interface';
import { ServicingFunctionIdType } from '@common/interfaces/servicing-function-id';
import { ServicingPageIdType } from '@common/interfaces/servicing-page-id';
import { LOAD_MASTERS } from '@common/master';
import { FlightMealService } from '@common/services/my-booking/flight-meal-service/flight-meal-service';
import { GetMealStoreService } from '@common/services/my-booking/get-meal-store/get-meal-store.service';
import { MyBookingServiceService } from '@common/services/my-booking/my-booking-service/my-booking-service.service';
import { MyBookingService } from '@common/services/my-booking/my-booking.service';
import { ServicingCommonStoreService } from '@common/services/servicing-common-store/servicing-common-store.service';
import { SupportModalBlockComponent } from '@lib/components/support-class';
import { DialogClickType } from '@lib/interfaces';
import { ErrorType } from '@lib/interfaces/errors';
import { AswMasterService, CommonLibService, PageInitService } from '@lib/services';
import { BehaviorSubject, combineLatest, Observable, of, Subscription, withLatestFrom } from 'rxjs';
import { Bound, GetMealResponse, GetMealResponseData, GetOrderResponseData, Traveler, Type1 } from 'src/sdk-servicing';
import { GetMealRequest } from 'src/sdk-servicing/model/getMealRequest';
import { FlightMealRequestPayload } from './flight-meal-request-payload.state';
import { ErrorCodeConstants } from '@conf/app.constants';

@Component({
  selector: 'asw-flight-meal-request',
  templateUrl: './flight-meal-request.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlightMealRequestComponent extends SupportModalBlockComponent {
  subPageId: string = ServicingPageIdType.FLIGHT_MEA_REQUEST;

  subFunctionId: string = ServicingFunctionIdType.SERVICE_REQUEST;

  public isSubmitEnable: boolean = false;

  public override payload: FlightMealRequestPayload | null = {};

  _subscriptions: Subscription = new Subscription();

  transitionSourceFunctionId: string = '';

  transitionSourcePageId: string = '';

  getMealRequest: GetMealRequest = {
    orderId: '',
    credential: {
      lastName: '',
      firstName: '',
    },
  };

  travelers!: Traveler[];
  travelersWithMeals!: Traveler[];
  flights!: Type1[];
  pnr!: GetOrderResponseData;
  meal!: GetMealResponseData;
  mealServicingCommon!: MealInformation;
  isMasterCallCompleted$ = new BehaviorSubject<boolean>(false);
  isApiServicingCommonCallCompleted$ = new BehaviorSubject<boolean>(false);
  isApiGetMealCallCompleted$ = new BehaviorSubject<boolean>(false);
  pageEndInitCalled: boolean = false;

  constructor(
    private _common: CommonLibService,
    private _router: Router,
    public change: ChangeDetectorRef,
    private _pageInitSvc: PageInitService,
    private _myBookingServiceService: MyBookingServiceService,
    private _servicingCommonStoreService: ServicingCommonStoreService,
    private _flightMealService: FlightMealService,
    private _myBookingService: MyBookingService,
    private _getMealStoreService: GetMealStoreService,
    private _aswMasterService: AswMasterService
  ) {
    super(_common);
    this.closeWithUrlChange(this._router);
    this.watchGetMealResultFailure();
    this._subscriptions.add(
      this._flightMealService._isEnableApplicationSubmit$.subscribe((isEnable: boolean) => {
        this.isSubmitEnable = isEnable;
      })
    );
  }
  mealResponse!: Observable<GetMealResponse>;
  getOrderResponse!: Observable<ServicingCommonModel>;
  init() {
    // (1.1) 画面の初期化処理(G01-003)の初期化開始処理を呼び出す。
    this._pageInitSvc.startInit();

    // (1.2) 画面情報に以下の内容を設定する
    this.setInitialAswCommon();

    this.createGetMealRequest();
    this._getMealStoreService.setGetMealFromApi(this.getMealRequest);

    // (1.3.2) サービシング共通store.機内食情報が存在する場合、サービシング共通store.機内食情報.セグメントマップを更新サービス情報.セグメントマップに設定する。
    // (1.5)「表示内容」シートに従い設定する。
    /**
     * [1.2] 機内食選択モーダル
     * <以下、サービシング共通store.PNR情報取得API応答.data.travelersの要素繰り返し>(それぞれ要素が当該搭乗者情報)
     * 機内食情報取得API応答.data.<当該搭乗者情報.Id>を搭乗者別機内食情報とする。搭乗者別機内食情報が存在する場合、以降の項目を設定する。
     *
     */
    // (1.4)	以下の内容で機内食情報取得APIを呼び出す。
    this._subscriptions.add(
      this._aswMasterService
        .load([LOAD_MASTERS.LISTDATA_ALL, LOAD_MASTERS.M_SPECIAL_MEAL, LOAD_MASTERS.AIRPORT_I18N_ALL], true)
        .subscribe((response) => {
          const [mListData, mSpecialMeal, airportI18nAll] = response;
          this._flightMealService.masterData$.next({
            ...this._flightMealService.masterData$,
            listData: parseEmptyArray(mListData),
            mSpecialMeal: parseEmptyArray(mSpecialMeal),
            airportI18nAll: airportI18nAll,
          });
          this.isMasterCallCompleted$.next(true);
        })
    );

    this._subscriptions.add(
      this._getMealStoreService.success$.subscribe((getMealResponse) => {
        this.meal = getMealResponse.model?.data ?? ({} as GetMealResponseData);
        this.isApiGetMealCallCompleted$.next(true);
      })
    );

    this._subscriptions.add(
      this._servicingCommonStoreService.getServicingCommonModel$().subscribe((servicingCommon) => {
        this.pnr = servicingCommon.getOrder as GetOrderResponseData;
        this.mealServicingCommon = servicingCommon.meal as Meal;
        this.travelers = (servicingCommon.getOrder as GetOrderResponseData)?.travelers ?? ([] as Array<Traveler>);
        this.flights =
          this.pnr?.air?.bounds?.flatMap((bound: Bound) => bound.flights ?? ([] as Type1[])) ?? ([] as Type1[]);
        this._flightMealService.flights = this.flights;
        this._flightMealService.travelers = this.travelers;
        this._flightMealService.pnr = this.pnr;
        this.isApiServicingCommonCallCompleted$.next(true);
      })
    );

    this._subscriptions.add(
      combineLatest([
        this.isApiGetMealCallCompleted$,
        this.isApiServicingCommonCallCompleted$,
        this.isMasterCallCompleted$,
      ]).subscribe(([a, b, c]) => {
        if (a && b && c) {
          this.travelersWithMeals = this.travelers.filter((traveler) => Object.keys(this.meal).includes(traveler?.id!));
          this._flightMealService.inFlightMealRequestData$.next({
            pnr: this.pnr,
            meal: this.meal,
            travelers: this.travelers,
          });

          this.mapStoreDataToService();
          this._flightMealService.mealInformationPre = this._flightMealService.deepClone(
            this._flightMealService.mealInformation
          );
          this._flightMealService.onHandleCommonNo5();
          this.change.markForCheck();

          this.onInitEnd();
        }
      })
    );
  }

  private watchGetMealResultFailure() {
    this._subscriptions.add(
      this._getMealStoreService.failure$
        .pipe(withLatestFrom(this._common.apiErrorResponseService.getApiErrorResponse$(), (_, state) => state))
        .subscribe((state) => {
          const apiErrorCode = this._common.apiError?.errors?.[0]?.code;
          const msgErrorCodes: { [key: string]: string } = {
            [ErrorCodeConstants.ERROR_CODES.EBAZ000157]: 'E0157',
            [ErrorCodeConstants.ERROR_CODES.EBAZ000254]: 'E1354',
            [ErrorCodeConstants.ERROR_CODES.EBAZ000722]: 'E1722',
          };

          if (apiErrorCode && apiErrorCode in msgErrorCodes) {
            this._common.errorsHandlerService.setNotRetryableError({
              errorType: ErrorType.BUSINESS_LOGIC,
              errorMsgId: msgErrorCodes[apiErrorCode],
              apiErrorCode: apiErrorCode,
            });
          } else {
            this._common.errorsHandlerService.setNotRetryableError({
              errorType: ErrorType.SYSTEM,
              apiErrorCode: apiErrorCode,
            });
          }

          this.onInitEnd();
        })
    );
  }

  mapStoreDataToService(): void {
    const segmentMap = new Map<string, SegmentMap>();
    const mealObj = this.mealServicingCommon ?? {};
    const exceptKey = ['totalAmount', 'mealDeleteFlg', 'mealInsertFlg', 'isApplicationProcessed', 'totalAmount'];
    for (const [segmentKey, segmentValue] of Object.entries(mealObj)) {
      if (segmentKey !== 'airBoundId' && segmentValue !== undefined) {
        const passengerApplicationStatusMap = new Map<string, PassengerApplicationStatusMap>();
        let mealCatalog: MealCatalogItem[] = [];
        for (const [innerKey, innerValue] of Object.entries(segmentValue as Record<string, any>)) {
          if (innerKey === 'mealCatalog') {
            mealCatalog = innerValue as MealCatalogItem[];
          } else {
            passengerApplicationStatusMap.set(innerKey, { ...innerValue });
          }
        }

        if (exceptKey.includes(segmentKey) !== true) {
          segmentMap.set(segmentKey, {
            passengerApplicationStatusMap,
            mealCatalog,
          });
        }
      }
    }
    const newStructure: UpdateFlightMealInformation = {
      airBoundId: this.mealServicingCommon?.airBoundId ?? '',
      segmentMap,
    };

    const newSize = newStructure.segmentMap?.size || 0;
    if (this._flightMealService.mealInformation.segmentMap && newStructure.segmentMap && newSize > 0) {
      const updatedSegmentMap = this.mergeDeep(
        Object.fromEntries(this._flightMealService.mealInformation.segmentMap),
        newStructure.segmentMap
      );
      this._flightMealService.mealInformation.segmentMap = new Map(Object.entries(updatedSegmentMap));
      this._flightMealService.mealInformation.airBoundId = newStructure.airBoundId;
    }
  }

  /** (1.2)	画面情報に以下の内容を設定する */
  setInitialAswCommon() {
    this._common.aswCommonStoreService.updateAswCommon({
      functionId: ServicingFunctionIdType.BOOKING_CONFIRMATION,
      pageId: ServicingPageIdType.MYBOOKING_SERVICE,
      subFunctionId: this.subFunctionId,
      subPageId: this.subPageId,
      isEnabledLogin: false,
      isUpgrade: false,
    });
  }

  onInitEnd() {
    if (!this.pageEndInitCalled) {
      this._pageInitSvc.subDynamicInit(of({}));
      this._pageInitSvc.endInit();
      this.pageEndInitCalled = true;
    }
  }

  createGetMealRequest() {
    const aswStoreService = this._common.aswServiceStoreService.aswServiceData;
    this.getMealRequest = {
      orderId: aswStoreService.orderId,
      credential: {
        lastName: aswStoreService.lastName ?? '',
        firstName: aswStoreService.firstName ?? '',
      },
    };
  }

  mergeDeep(target: any, source: Map<any, any>): any {
    source.forEach((value, key) => {
      if (value instanceof Map) {
        if (!target[key]) {
          target[key] = {};
        }
        target[key] = this.mergeDeep(target[key], value);
      } else {
        target[key] = value;
      }
    });
    return target;
  }

  /** (2) 閉じるボタン押下処理※機内食選択モーダル */
  async clickClose() {
    // (2.1) 更新サービス情報.セグメントマップ≠サービシング共通store.機内食情報.セグメントマップの場合、
    if (this.isSubmitEnable) {
      const clickType = await this._myBookingServiceService.openDialogPromise('MSG0524');
      if (clickType === DialogClickType.CONFIRM) {
        this._flightMealService.mealInformation = this._flightMealService.deepClone(
          this._flightMealService.mealInformationPre
        );
        this.clickCloseSub();
      }
    } else {
      // (2.2) 上記以外の場合、当モーダルを閉じる。
      this.clickCloseSub();
    }
  }

  /** 閉じる処理 */
  clickCloseSub() {
    this.close();
  }

  // (4) 申込ボタン※機内食選択モーダル
  async clickSubmit() {
    const isChildMealAvailabel = this.submitStep1();
    // 1.7.	チャイルドミール有無=trueの場合、パラメータとして"MSG0459"(選択した特別機内食が付添人(大人、ヤングアダルト)に提供される注意を促す旨)を確認ダイアログ(G02-005)に渡し、表示する。
    if (isChildMealAvailabel) {
      const clickType = await this._myBookingServiceService.openDialogPromise('MSG0459');
      // (4.1.7.1) 確認ダイアログより閉じるボタン押下が返却された場合、処理を終了する。
      if (clickType === DialogClickType.CLOSE) {
        return;
      }
    }
    const { isNew, isDeleted } = this.submitStep2();
    // (4.3) サービスを追加する旨(メッセージID:"MSG0523")を確認ダイアログ(G02-005)に渡し、表示する。
    const clickType = await this._myBookingServiceService.openDialogPromise('MSG0523');
    if (clickType === DialogClickType.CONFIRM) {
      this.submitStep3(isNew, isDeleted);
    }
  }

  submitStep1(): boolean {
    // (4.1) 選択した機内食情報にチャイルドミールが存在するか判定する。
    // (4.1.1) チャイルドミール有無にfalseを設定する。
    // チャイルドミール有無に
    let isChildMealAvailabel = false;
    isChildMealAvailabel = this.flights?.some((flight) => {
      if (this._flightMealService.mealInformation.segmentMap?.has(flight?.id!)) {
        return this.travelersWithMeals?.some((traveler) => {
          // 1.4.1.	搭乗者情報.accompanyingTravelerId = “”(空欄)(同伴搭乗者がいない)の場合、次の更新搭乗者情報の繰り返し処理を行う。
          // 1.4.2.	上記以外　かつ　搭乗者情報.passengerTypeCode＝"ADT"の場合、以降の処理を継続する。
          if (traveler.accompanyingTravelerId && traveler.passengerTypeCode === Traveler.PassengerTypeCodeEnum.Adt) {
            // 1.5.2.	上記以外の場合、当該搭乗者.idをキーとする対象セグメントマップ.搭乗者別申込状況マップを対象搭乗者申込マップとして保持とする。
            const passengerApplicationStatusMap = this.getPassengerApplicationStatusMap(flight?.id!, traveler?.id!);
            // 1.6.1.	対象搭乗者申込マップ.SSRコード="CHML"(チャイルドミール)の場合、チャイルドミール有無にtrueを設定し、繰り返し処理を終了する。
            return passengerApplicationStatusMap?.ssrCode === TravelerMealInfo.MealCode.CHML;
          }
          return false;
        });
      }
      return false;
    });

    return isChildMealAvailabel;
  }

  submitStep2(): { isNew: boolean; isDeleted: boolean } {
    // (4.2) 有料機内食の申込種別判定を行う。
    // (4.2.1) 新規申込有無にfalse(未申込)を設定する
    // 新規申込有無に
    let isNew = false;
    // 削除申込有無に
    let isDeleted = false;
    this.flights?.forEach((flight) => {
      // 2.4.	上記以外の場合、当該セグメント情報.idをキーとする更新サービス情報.セグメントマップを対象セグメントマップとして保持する。
      if (this._flightMealService.mealInformation.segmentMap?.has(flight?.id!)) {
        this.travelers.forEach((traveler) => {
          if (isNew && isDeleted) {
            return;
          }
          const passengerApplicationStatusMap = this.getPassengerApplicationStatusMap(flight?.id!, traveler?.id!);
          //  2.7.	上記以外の場合、以下の処理にて申込済みの機内食情報と比較し、有料機内食の申込種別を判定する。
          if (
            passengerApplicationStatusMap &&
            passengerApplicationStatusMap.mealType === TravelerMealInfo.MealType.ANCILLARY
          ) {
            // 2.7.1. 対象搭乗者申込マップ.更新区分 = "cancel"(削除)の場合、削除申込有無にtrue(申込有)を設定する。
            if (passengerApplicationStatusMap.updateCategory === TravelerMealInfo.UpdateCategory.CANCEL) {
              isDeleted = true;
            }
            // 2.7.2.	上記以外の場合、新規申込有無にtrue(申込有)を設定する。
            else {
              isNew = true;
            }
          }
        });
      }
    });
    return {
      isNew,
      isDeleted,
    };
  }

  private getPassengerApplicationStatusMap(flightId: string, travelerId: string) {
    const segmentMap = this._flightMealService.mealInformation.segmentMap?.get(flightId);
    if (segmentMap?.passengerApplicationStatusMap.has(travelerId)) {
      return segmentMap?.passengerApplicationStatusMap.get(travelerId);
    }
    return undefined;
  }

  private getTravelerMealInfo(flightId: string, travelerId: string): TravelerMealInfo | undefined {
    const meal = this.pnr?.services?.meal as Meal;
    const travelerMeal = meal?.[flightId]?.[travelerId] as TravelerMealInfo;
    return isEmptyObject(travelerMeal) !== true ? travelerMeal : undefined;
  }

  private shouldSetIsDeleted(
    travelerMealInfo: TravelerMealInfo,
    passengerApplicationStatusMap: PassengerApplicationStatusMap
  ): boolean {
    // 2.7.5.1.	対象申込済搭乗者情報.appliedMealList.type="ancillary"(有料機内食)
    // 2.7.5.2.	対象申込済搭乗者情報.appliedMealList.code≠対象搭乗者申込マップ.SSRコード
    // 2.7.5.3.	対象申込済搭乗者情報.appliedMealList.serviceStatus≠("cancelled"(取り消し) かつ、"prebooked"(prebook))
    return travelerMealInfo?.appliedMealList?.every((item: AppliedMealList) => {
      return (
        item?.code !== passengerApplicationStatusMap.ssrCode &&
        item?.type !== TravelerMealInfo.MealType.ANCILLARY &&
        item?.serviceStatus !== TravelerMealInfo.ServiceStatusEnum.Cancelled &&
        item?.serviceStatus !== TravelerMealInfo.ServiceStatusEnum.Prebooked
      );
    });
  }

  submitStep3(isNew: boolean, isDeleted: boolean) {
    const meal = this.createMealUpdate(isNew, isDeleted);
    // (4.3.4) 以下の内容をサービシング共通storeで保持する。
    this._servicingCommonStoreService.updateServicingCommon({
      // 登録完了時機能種別
      completedFunction: '',
      // 初回表示フラグ
      isFirstDisplay: false,
      // 初期表示サービス
      initDisplayService: 'meal',
      meal: meal,
    });

    // (4.3.5) 機内食選択モーダルを閉じ、予約詳細(S01-P030)の[初期表示処理]を呼び出す。
    this._myBookingService.runOnInit();
    this.clickCloseSub();
    this._flightMealService._isEnableApplicationSubmit$.next(false);
  }

  createMealUpdate(isNew: boolean, isDeleted: boolean): MealInformation {
    let segments: Partial<{
      [segmentId: string]: {
        [key2: string]: PassengerApplicationStatusMap | MealCatalogItem[] | undefined;
        mealCatalog?: MealCatalogItem[];
      };
    }> = {};
    let totalAmount = 0;
    const segmentMap = this._flightMealService.mealInformation?.segmentMap;
    for (const [flightId, flightInfo] of segmentMap! ?? []) {
      for (const [travelerId, travelerInfo] of flightInfo?.passengerApplicationStatusMap ?? []) {
        totalAmount += travelerInfo?.paidServiceAmount ?? 0;
        segments = {
          ...segments,
          [flightId]: {
            ...segments[flightId],
            [travelerId]: {
              ...travelerInfo,
              ssrCode: travelerInfo?.babyMealSelectionFlag ? TravelerMealInfo.MealCode.BBML : travelerInfo?.ssrCode,
            },
            mealCatalog: [
              ...(segments[flightId]?.mealCatalog ?? []),
              ...(flightInfo?.mealCatalog?.length
                ? [
                    {
                      ssrCode:
                        flightInfo?.mealCatalog?.find((item) => item?.ssrCode === travelerInfo?.ssrCode)?.ssrCode ??
                        undefined,
                      quota:
                        flightInfo?.mealCatalog?.find((item) => item?.ssrCode === travelerInfo?.ssrCode)?.quota ??
                        undefined,
                    },
                  ]
                : []),
            ],
          },
        };
      }
    }
    const meal = {
      airBoundId: this.pnr?.air?.bounds?.[0]?.airBoundId ?? '',
      ...segments,
      totalAmount: totalAmount > 0 ? totalAmount : undefined,
      isApplicationProcessed: true,
      mealInsertFlg: isNew,
      mealDeleteFlg: isDeleted,
    };
    return meal;
  }

  hasMeal(travelerId?: string) {
    return isEmptyObject(this.meal?.[travelerId!]) !== true;
  }
  destroy() {
    this._servicingCommonStoreService.updateServicingCommon({
      previousPageIdentifier: PREVIOUS_PAGE_IDENTIFIER.FLIGHT_MEA_REQUEST,
    });
    this._subscriptions?.unsubscribe();
  }

  reload() {}
}
